import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import PageChat from "../components/PageChat";
import {
    ArrowLeft,
    TrendingUp,
    TrendingDown,
    Coins,
    Lock,
    Bitcoin,
    Sparkles,
    Trophy,
    XCircle,
    Loader2,
    AlertCircle,
    Users,
} from "lucide-react";

function fmtUsd(n) {
    if (n == null) return "—";
    return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
}

function Countdown({ to, onExpire }) {
    const calc = () => Math.max(0, Math.round((new Date(to).getTime() - Date.now()) / 1000));
    const [remain, setRemain] = useState(calc);
    useEffect(() => {
        setRemain(calc());
        const t = setInterval(() => {
            const r = calc();
            setRemain(r);
            if (r <= 0) {
                clearInterval(t);
                onExpire && onExpire();
            }
        }, 1000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [to]);
    if (remain <= 0) return <span className="font-heading font-bold text-vibrantorange">Settling…</span>;
    const m = Math.floor(remain / 60);
    const s = remain % 60;
    return <span className="font-heading font-black text-ink tabular-nums">{m}:{s.toString().padStart(2, "0")}</span>;
}

function PoolBar({ up, down }) {
    const total = up + down;
    const upPct = total === 0 ? 50 : (up / total) * 100;
    return (
        <div className="w-full h-2.5 bg-cream border-[2px] border-ink rounded-full overflow-hidden">
            <div className="h-full bg-[#2563EB]" style={{ width: `${upPct}%` }} />
        </div>
    );
}

export default function BetCrypto() {
    const { user, loading: authLoading } = useAuth();
    const [prices, setPrices] = useState(null);
    const [credits, setCredits] = useState(null);
    const [rounds, setRounds] = useState([]);
    const [selectedRoundId, setSelectedRoundId] = useState(null);
    const [direction, setDirection] = useState("UP");
    const [stake, setStake] = useState(1);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [bets, setBets] = useState([]);

    const isLoggedIn = user && user !== false;

    const loadPrices = useCallback(async () => {
        try {
            const { data } = await api.get("/bets/prices");
            setPrices(data);
        } catch {}
    }, []);

    const loadBalance = useCallback(async () => {
        if (!isLoggedIn) return;
        try {
            const { data } = await api.get("/payments/i2v/balance");
            setCredits(data.credits);
        } catch {}
    }, [isLoggedIn]);

    // Default-select first OPEN round; if selected is closed, switch
    const loadRounds = useCallback(async () => {
        try {
            const { data } = await api.get("/rounds");
            setRounds(data);
            setSelectedRoundId((prev) => {
                const cur = data.find((r) => r.id === prev);
                if (cur && cur.status === "OPEN") return prev;
                const firstOpen = data.find((r) => r.status === "OPEN");
                return firstOpen ? firstOpen.id : (data[0]?.id || null);
            });
        } catch {}
    }, []);

    const loadBets = useCallback(async () => {
        if (!isLoggedIn) return;
        try {
            const { data } = await api.get("/bets");
            setBets(data);
        } catch {}
    }, [isLoggedIn]);

    useEffect(() => { loadPrices(); loadRounds(); }, [loadPrices, loadRounds]);
    useEffect(() => {
        const t = setInterval(() => { loadPrices(); loadRounds(); }, 5000);
        return () => clearInterval(t);
    }, [loadPrices, loadRounds]);
    useEffect(() => { loadBalance(); loadBets(); }, [loadBalance, loadBets]);
    useEffect(() => {
        if (!isLoggedIn) return;
        const t = setInterval(() => { loadBets(); loadBalance(); }, 6000);
        return () => clearInterval(t);
    }, [isLoggedIn, loadBets, loadBalance]);

    const selected = useMemo(
        () => rounds.find((r) => r.id === selectedRoundId) || null,
        [rounds, selectedRoundId]
    );

    const potentialPayout = useMemo(() => {
        if (!selected || stake < 1) return 0;
        const sideTotal = direction === "UP" ? selected.total_up : selected.total_down;
        const oppTotal = direction === "UP" ? selected.total_down : selected.total_up;
        // After I place my stake, the side total includes me; total pool also includes me.
        const newSide = sideTotal + stake;
        const newPool = sideTotal + oppTotal + stake;
        if (oppTotal === 0) return stake; // refund (no opponents)
        // My share = stake / newSide * newPool
        return Math.floor((stake / newSide) * newPool);
    }, [selected, direction, stake]);

    const place = async (e) => {
        e.preventDefault();
        setError("");
        if (!isLoggedIn || !selected) return;
        if (stake < 1) return setError("Stake must be at least 1 credit");
        if (credits != null && stake > credits) return setError("Not enough credits");
        setSubmitting(true);
        try {
            await api.post("/bets", { round_id: selected.id, direction, stake });
            await Promise.all([loadBalance(), loadBets(), loadRounds()]);
        } catch (e2) {
            setError(formatApiError(e2.response?.data?.detail) || e2.message);
        } finally {
            setSubmitting(false);
        }
    };

    const active = bets.filter((b) => b.status === "ACTIVE");
    const settled = bets.filter((b) => b.status === "SETTLED");
    const stats = settled.reduce(
        (a, b) => {
            a.wins += b.result === "WIN" ? 1 : 0;
            a.losses += b.result === "LOSS" ? 1 : 0;
            a.refunds += b.result === "REFUND" ? 1 : 0;
            a.staked += b.stake;
            a.won += b.payout || 0;
            return a;
        },
        { wins: 0, losses: 0, refunds: 0, staked: 0, won: 0 }
    );

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
            <Link to="/" data-testid="bet-back-link" className="inline-flex items-center gap-1.5 font-heading font-bold text-xs uppercase tracking-widest text-ink/60 hover:text-vibrantorange transition-colors mb-3">
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} />
                Back to Endfb
            </Link>

            <div className="flex gap-2 mb-4">
                <Link to="/bet-crypto" data-testid="bet-tab-crypto" className="brutal-btn bg-vibrantorange text-white text-xs py-2 px-3">
                    Crypto Prices
                </Link>
                <Link to="/bet-markets" data-testid="bet-tab-p2p" className="brutal-btn bg-white text-ink text-xs py-2 px-3">
                    P2P Bets
                </Link>
            </div>

            <div className="flex items-start gap-3 mb-6">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-vibrantorange border-[3px] border-ink rounded-2xl flex items-center justify-center shadow-brutal-lg flex-shrink-0">
                    <Bitcoin className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={2.5} />
                </div>
                <div className="flex-1">
                    <h1 className="font-heading font-black text-3xl sm:text-5xl tracking-tighter text-ink leading-tight">
                        Bet <span className="text-vibrantorange">crypto</span> &amp; <span className="text-vibrantorange">stocks</span>
                    </h1>
                    <p className="font-body text-ink/70 mt-1">
                        Pool credits against other users on BTC, ETH, META &amp; NVDA. Winners split the pot.
                    </p>
                </div>
                {isLoggedIn && credits != null && (
                    <div data-testid="bet-credits-chip" className="hidden sm:flex items-center gap-1.5 bg-vibrantyellow border-[3px] border-ink rounded-xl px-3 py-2 shadow-brutal flex-shrink-0">
                        <Coins className="w-4 h-4 text-ink" strokeWidth={2.5} />
                        <span className="font-heading font-bold text-sm text-ink">{credits} credit{credits === 1 ? "" : "s"}</span>
                    </div>
                )}
            </div>

            {/* Live prices */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="bet-prices">
                {["BTC", "ETH", "META", "NVDA"].map((a) => {
                    const p = prices?.[a];
                    const positive = p && p.change_24h_pct >= 0;
                    const isStock = p?.kind === "stock";
                    const closed = isStock && p.market_state && p.market_state !== "REGULAR";
                    return (
                        <div key={a} className="brutal-card p-4 bg-white">
                            <div className="flex items-center justify-between gap-2">
                                <span className="font-heading font-black text-base text-ink tracking-tight">{a}</span>
                                <span className={`font-heading font-bold text-[10px] uppercase tracking-widest border-[2px] border-ink rounded-md px-1.5 py-0.5 ${positive ? "bg-[#2563EB] text-white" : "bg-vibrantpink text-white"}`}>
                                    {positive ? "+" : ""}{p ? p.change_24h_pct.toFixed(2) : "—"}%
                                </span>
                            </div>
                            <div className="font-heading font-black text-2xl text-ink mt-1 tabular-nums">{p ? fmtUsd(p.price) : "…"}</div>
                            <div className="font-body text-[10px] text-ink/50 uppercase tracking-widest mt-0.5 flex items-center gap-1">
                                {isStock ? (closed ? `Stock · ${p.market_state.toLowerCase()}` : "Stock · open") : "Crypto · 24/7"}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Rounds grid */}
            <div className="mb-6">
                <h2 className="font-heading font-black text-2xl text-ink tracking-tight mb-3 flex items-center gap-2">
                    <Users className="w-5 h-5 text-vibrantorange" strokeWidth={2.5} /> Live rounds
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="rounds-grid">
                    {rounds.map((r) => {
                        const isSel = r.id === selectedRoundId;
                        const total = r.total_up + r.total_down;
                        const isOpen = r.status === "OPEN";
                        const isLive = r.status === "LIVE";
                        const phaseTo = isOpen ? r.bet_close_at : r.expires_at;
                        return (
                            <button
                                key={r.id}
                                type="button"
                                onClick={() => isOpen && setSelectedRoundId(r.id)}
                                disabled={!isOpen}
                                data-testid={`round-card-${r.asset}-${r.duration_sec}`}
                                className={`text-left brutal-card p-4 transition-all ${isSel ? "bg-vibrantyellow" : isLive ? "bg-cream" : "bg-white hover:bg-cream"} ${!isOpen ? "opacity-90 cursor-not-allowed" : ""}`}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="font-heading font-black text-base text-ink">{r.asset}</span>
                                    <span className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60">{r.duration_label}</span>
                                </div>
                                <div className="mt-2 mb-2"><PoolBar up={r.total_up} down={r.total_down} /></div>
                                <div className="flex items-center justify-between text-[10px] font-heading font-bold uppercase tracking-widest">
                                    <span className="text-[#2563EB]">UP {r.total_up}</span>
                                    <span className="text-vibrantpink">{r.total_down} DOWN</span>
                                </div>
                                <div className="flex items-center justify-between mt-2 gap-2">
                                    <span className={`font-heading font-bold text-[9px] uppercase tracking-widest border-[2px] border-ink rounded-md px-1.5 py-0.5 ${isOpen ? "bg-[#2563EB] text-white" : "bg-vibrantorange text-white"}`}>
                                        {isOpen ? "Bet open" : "Live"}
                                    </span>
                                    <Countdown to={phaseTo} onExpire={() => { loadRounds(); loadBets(); }} />
                                </div>
                                <div className="font-body text-[10px] text-ink/60 mt-1">
                                    Pool {total} cr{r.strike_price ? ` · strike ${fmtUsd(r.strike_price)}` : ""}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {authLoading ? (
                <div className="font-heading font-bold text-ink/60">Loading…</div>
            ) : !isLoggedIn ? (
                <div data-testid="bet-login-gate" className="brutal-card p-8 bg-white max-w-lg">
                    <div className="w-12 h-12 bg-vibrantorange border-[3px] border-ink rounded-2xl flex items-center justify-center shadow-brutal mb-4">
                        <Lock className="w-6 h-6 text-white" strokeWidth={2.5} />
                    </div>
                    <h2 className="font-heading font-black text-3xl text-ink tracking-tighter mb-2">Log in to bet</h2>
                    <p className="font-body text-ink/70 mb-5">You need credits to join a round.</p>
                    <div className="flex flex-wrap gap-3">
                        <Link to="/login" className="brutal-btn-primary">Log in</Link>
                        <Link to="/register" className="brutal-btn-secondary">Create account</Link>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Place bet form */}
                    <form onSubmit={place} className="lg:col-span-5 space-y-4" data-testid="bet-form">
                        {selected ? (
                            <div className="brutal-card p-4 bg-cream">
                                <div className="font-heading font-bold text-xs uppercase tracking-widest text-ink/60 mb-1">Joining</div>
                                <div className="font-heading font-black text-xl text-ink">{selected.asset} · {selected.duration_label}</div>
                                <div className="font-body text-xs text-ink/70 mt-1">
                                    Strike {fmtUsd(selected.strike_price)} · Pool {selected.total_up + selected.total_down} cr
                                </div>
                            </div>
                        ) : (
                            <div className="brutal-card p-4 bg-cream font-body text-sm text-ink/60">Pick a round above</div>
                        )}

                        <div>
                            <label className="font-heading font-bold text-xs uppercase tracking-widest text-ink mb-2 block">Direction</label>
                            <div className="grid grid-cols-2 gap-2" data-testid="bet-direction-group">
                                <button type="button" onClick={() => setDirection("UP")} data-testid="bet-dir-up" className={`brutal-btn py-3 ${direction === "UP" ? "bg-[#2563EB] text-white" : "bg-white text-ink"}`}>
                                    <TrendingUp className="w-4 h-4 mr-1.5" strokeWidth={2.5} />UP
                                </button>
                                <button type="button" onClick={() => setDirection("DOWN")} data-testid="bet-dir-down" className={`brutal-btn py-3 ${direction === "DOWN" ? "bg-vibrantpink text-white" : "bg-white text-ink"}`}>
                                    <TrendingDown className="w-4 h-4 mr-1.5" strokeWidth={2.5} />DOWN
                                </button>
                            </div>
                        </div>

                        <div>
                            <div className="flex items-end justify-between mb-2">
                                <label className="font-heading font-bold text-xs uppercase tracking-widest text-ink">Stake (credits)</label>
                                <span className="font-body text-xs text-ink/60">Balance: {credits ?? "…"}</span>
                            </div>
                            <div className="flex items-stretch gap-2">
                                <button type="button" onClick={() => setStake((s) => Math.max(1, s - 1))} data-testid="bet-stake-minus" className="bg-white border-[3px] border-ink rounded-xl w-11 font-heading font-black text-xl shadow-brutal hover:-translate-y-0.5 transition-all">−</button>
                                <input
                                    type="number"
                                    min={1}
                                    value={stake}
                                    onChange={(e) => {
                                        const v = parseInt(e.target.value, 10);
                                        if (Number.isNaN(v)) return setStake(1);
                                        setStake(Math.max(1, v));
                                    }}
                                    data-testid="bet-stake-input"
                                    className="flex-1 bg-white border-[3px] border-ink rounded-xl font-heading font-black text-xl text-ink text-center shadow-brutal focus:outline-none"
                                />
                                <button type="button" onClick={() => setStake((s) => s + 1)} data-testid="bet-stake-plus" className="bg-white border-[3px] border-ink rounded-xl w-11 font-heading font-black text-xl shadow-brutal hover:-translate-y-0.5 transition-all">+</button>
                            </div>
                            <div className="flex gap-1.5 mt-2" data-testid="bet-stake-presets">
                                {[1, 5, 10, "max"].map((p) => {
                                    const v = p === "max" ? Math.max(1, credits || 1) : p;
                                    return (
                                        <button key={p} type="button" onClick={() => setStake(v)} data-testid={`bet-stake-preset-${p}`} className="flex-1 font-heading font-bold text-xs uppercase tracking-widest border-[2px] border-ink rounded-lg py-1.5 bg-white text-ink hover:bg-cream">
                                            {p === "max" ? "MAX" : p}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="brutal-card p-4 bg-cream">
                            <div className="flex items-center justify-between">
                                <span className="font-heading font-bold text-xs uppercase tracking-widest text-ink/60">If you win</span>
                                <span className="font-heading font-black text-2xl text-ink">~{potentialPayout} cr</span>
                            </div>
                            <div className="font-body text-[10px] text-ink/50 mt-1">
                                Estimated share of the pool at current sizes. Updates as more bets come in.
                            </div>
                        </div>

                        {error && (
                            <div data-testid="bet-error" className="flex items-start gap-2 bg-vibrantpink/15 border-[3px] border-vibrantpink text-ink rounded-xl p-3">
                                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-vibrantpink" strokeWidth={2.5} />
                                <span className="font-body font-semibold text-sm">{error}</span>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={submitting || !selected || selected.status !== "OPEN" || credits === 0}
                            className="brutal-btn-primary w-full text-base py-3.5"
                            data-testid="bet-submit-button"
                        >
                            <Sparkles className="w-4 h-4 mr-2" strokeWidth={2.5} />
                            {submitting ? "Placing…" : credits === 0 ? "Buy credits first" : selected ? (selected.status !== "OPEN" ? "Betting closed for this round" : `Join ${selected.asset} ${direction} with ${stake} cr`) : "Pick a round"}
                        </button>

                        {credits === 0 && (
                            <Link to="/buy-credits" className="brutal-btn-secondary w-full text-sm py-2">
                                <Coins className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.5} />
                                Buy credits
                            </Link>
                        )}
                    </form>

                    <div className="lg:col-span-5 mt-6">
                        <PageChat room="betting" title="Bettors' chat" />
                    </div>

                    {/* Active + History */}
                    <div className="lg:col-span-7 space-y-6">
                        <div className="brutal-card p-5 bg-white">
                            <h2 className="font-heading font-black text-lg text-ink mb-3 flex items-center gap-2">
                                <Loader2 className="w-5 h-5 text-vibrantorange animate-spin" strokeWidth={2.5} />
                                Live bets ({active.length})
                            </h2>
                            {active.length === 0 ? (
                                <p data-testid="bet-active-empty" className="font-body text-sm text-ink/50">No live bets. Join a round to get started.</p>
                            ) : (
                                <div className="space-y-2" data-testid="bet-active-list">
                                    {active.map((b) => {
                                        const ru = b.round_total_up ?? 0;
                                        const rd = b.round_total_down ?? 0;
                                        const sideTotal = b.direction === "UP" ? ru : rd;
                                        const oppTotal = b.direction === "UP" ? rd : ru;
                                        const share = sideTotal > 0 ? Math.floor((b.stake / sideTotal) * (sideTotal + oppTotal)) : b.stake;
                                        return (
                                            <div key={b.id} data-testid={`bet-active-${b.id}`} className="bg-cream border-[3px] border-ink rounded-xl p-3 shadow-brutal flex items-center justify-between gap-3">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <div className={`w-9 h-9 rounded-full border-2 border-ink flex items-center justify-center ${b.direction === "UP" ? "bg-[#2563EB]" : "bg-vibrantpink"}`}>
                                                        {b.direction === "UP" ? <TrendingUp className="w-4 h-4 text-white" strokeWidth={3} /> : <TrendingDown className="w-4 h-4 text-white" strokeWidth={3} />}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="font-heading font-bold text-sm text-ink">{b.asset} · {b.direction} · {b.duration_label}</div>
                                                        <div className="font-body text-xs text-ink/60 truncate">Stake {b.stake} · pool {ru + rd} cr · win ~{share}</div>
                                                    </div>
                                                </div>
                                                <div className="text-right flex-shrink-0">
                                                    <Countdown to={b.expires_at} onExpire={() => { loadBets(); loadBalance(); loadRounds(); }} />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div className="brutal-card p-5 bg-white">
                            <h2 className="font-heading font-black text-lg text-ink mb-3 flex items-center gap-2">
                                <Trophy className="w-5 h-5 text-vibrantorange" strokeWidth={2.5} />
                                History
                                {settled.length > 0 && (
                                    <span className="ml-auto font-heading font-bold text-xs uppercase tracking-widest text-ink/50">
                                        {stats.wins}W · {stats.losses}L · {stats.refunds}T · net {stats.won - stats.staked >= 0 ? "+" : ""}{stats.won - stats.staked} cr
                                    </span>
                                )}
                            </h2>
                            {settled.length === 0 ? (
                                <p data-testid="bet-history-empty" className="font-body text-sm text-ink/50">No settled bets yet.</p>
                            ) : (
                                <div className="space-y-2 max-h-[480px] overflow-y-auto" data-testid="bet-history-list">
                                    {settled.map((b) => {
                                        const w = b.result === "WIN";
                                        const r = b.result === "REFUND";
                                        return (
                                            <div key={b.id} data-testid={`bet-settled-${b.id}`} className="bg-cream border-[3px] border-ink rounded-xl p-3 shadow-brutal flex items-center justify-between gap-3">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <div className={`w-9 h-9 rounded-full border-2 border-ink flex items-center justify-center ${w ? "bg-vibrantyellow" : r ? "bg-white" : "bg-ink"}`}>
                                                        {w ? <Trophy className="w-4 h-4 text-ink" strokeWidth={3} /> : r ? <Coins className="w-4 h-4 text-ink" strokeWidth={3} /> : <XCircle className="w-4 h-4 text-white" strokeWidth={3} />}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="font-heading font-bold text-sm text-ink">{b.asset} · {b.direction} · {b.duration_label}</div>
                                                        <div className="font-body text-xs text-ink/60 truncate">{fmtUsd(b.strike_price)} → {fmtUsd(b.close_price)}</div>
                                                    </div>
                                                </div>
                                                <div className="text-right flex-shrink-0">
                                                    <div className={`font-heading font-black text-lg ${w ? "text-[#2563EB]" : r ? "text-ink" : "text-vibrantpink"}`}>
                                                        {w ? `+${b.payout}` : r ? `±${b.stake}` : `-${b.stake}`}
                                                    </div>
                                                    <div className="font-body text-[10px] uppercase tracking-widest text-ink/50">{b.result}</div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
